import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';

import 'slick-carousel/slick/slick.css';

import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { imageSize } from '@common/react/utils/utils';
import { List } from '@common/typescript/objects/List';

import { DoctorLocation } from '@commonTuna/react/objects/DoctorLocation';
import { DoctorProfession } from '@commonTuna/react/objects/DoctorProfession';
import { Doctor, PageDoctor } from '@commonTuna/react/objects/BaseDoctor';
import { Review } from '@commonTuna/react/objects/Review';

import ServerPageProvider from '@app/components/UI/SeverPageProviderWithLang/ServerpageProviderWithLang';
import { SimpleBreadCrumbs } from '@app/components/UI/SimpleBreadcrumbs/SimpleBreadcrumbs';
import ImageWithSkeleton from '@app/components/UI/ImageWithSkeleton/ImageWithSkeleton';
import '@app/scss/pages/doctorPage.scss';
import { ApplicationState } from '@app/store';
import ShareLinks from '@app/components/UI/ShareLinks/ShareLinks';
import LocationName from '@app/components/UI/LocationName/LocationName';
import ContactUsForm from '@app/components/Forms/ContactUs/ContactUsForm';
import '@app/scss/components/slick-theme.scss';
import Reviews from '@app/components/UI/Reviews/Reviews';
import PageDoctors from '@app/components/Pages/DoctorList/PageDoctors';
import SocialIcons from '@app/components/UI/SocialIcons/SocialIcons';

interface DoctorWithRandomReviews extends Doctor {
	randomReviews: Array<Review>;
	countPages: number;
}

interface DoctorInnerProps {
	doctor: DoctorWithRandomReviews;
}

const DoctorInner: React.FC<DoctorInnerProps> = ({
	doctor,
}) => {
	const { i18n, t } = useTranslation();
	const isEnglish = i18n.language === 'en';

	const name = isEnglish ? doctor.nameEn : doctor.nameEs;
	const offices = useSelector((state: ApplicationState) => state.offices.items, shallowEqual);

	return (
		<div className="doctor-page inner-page">
			<div className="container is-relative">
				<div className="clearfix">
					<SimpleBreadCrumbs
						title={name}
						links={[
						// {titleKey: 'seo.practicePage.title', pathKey: 'seo.practicePage.link'},
							{ titleKey: 'seo.doctorsPage.title', pathKey: 'seo.doctorsPage.link' },
						]}
					/>
				</div>
				<div className="clearfix">
					<div className="doctor-page__image">
						<div className="square-block">
							<div className="doctor-page__image_inner">
								<img src={doctor.originalAvatar} alt="Doctor avatar" />
								{doctor.certificates?.length > 0
									&& <div className="doctor-certificates">
										{doctor.certificates
											.filter((item) => item.certificate.image)
											.map((item, i) =>
												<div key={item.id} className="certificate-wrapper">
													<div>
														<ImageWithSkeleton
															alt={item.certificate.name}
															key={item.id}
															src={imageSize(item.certificate.image, '_thumb')}
														/>
													</div>
												</div>)}
									</div>
								}
							</div>
						</div>
					</div>
					<div className="doctor-page__body">
						<div>
							<SocialIcons
								className="doctor-page__icons"
								socialMedias={doctor.socialMedias}
							/>
						</div>
						<div className="doctor-page__name inner-page__headline">
							<h1>
								{name}
							</h1>
						</div>
						<div className="clearfix doctor-page__locations">
							<div className="doctor-page__professions inline-tags">
								{doctor.professions && doctor.professions.map((profession: DoctorProfession, index: number) => (
									<div key={profession.id} className="pull-left">
										{(isEnglish ? profession.profession.nameEn : profession.profession.nameEs)
											+ (index !== doctor.professions.length - 1 ? ', ' : '')}
									</div>
								))}
							</div>
							<div className="inline-tags">
								{doctor.locations && doctor.locations.map((location: DoctorLocation, index: number) =>
									<span className="inline-tag pull-right" key={location.id}>
										<LocationName item={offices.find((office) => office.id === location.location.id) || location.location} />
									</span>)}
							</div>
						</div>
						<div
							className="doctor-page__description external-html"
							dangerouslySetInnerHTML={{ __html: isEnglish ? doctor.textEn : doctor.textEs }}
						/>
						<div className="share-links-container">
							<ShareLinks
								size={75}
								other={doctor?.googleReviewPlaceID
									&& <a
										className="btn btn-default google-button"
										style={{ marginLeft: 10 }}
										href={`https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${doctor?.googleReviewPlaceID}`}
										target="_blank"
										rel="noreferrer"
									>
										<i className="fa fa-google" aria-hidden="true" style={{ marginRight: 10 }} />
									</a>}
							/>
						</div>
					</div>
				</div>
				<PageDoctors
					items={{ list: doctor.pages, count: doctor.countPages } as List<PageDoctor>}
					doctorId={doctor.id}
				/>
				<Reviews reviews={doctor.randomReviews} />
				<div className="contacts-page__form">
					<div className="inner-page__headline text-center">
						<h2>{t('site.Schedule an Appointment Now')}</h2>
					</div>
					<ContactUsForm className="general-form" doctor={doctor} />
				</div>
			</div>
		</div>
	);
};

const DoctorPageComponent: React.FC = () => {
	const { path } = useParams<{ path }>();

	return (
		<ServerPageProvider
			path="doctor"
			inner={(serverPage) =>
				<ItemProvider
					id={serverPage?.item?.id || 0}
					type="doctor"
					readonly
					additionalParams={{ path }}
					loadRequest="getDoctorViewRemote"
					item={serverPage?.item}
					skipInitLoad={!!serverPage?.item}
				>
					{({ state: { item } }) => <DoctorInner
						doctor={item}
					/>}
				</ItemProvider>
			}
		/>
	);
};

export default DoctorPageComponent;
